import { useEffect, useMemo, useState } from "react";
import { EntityId } from "@reduxjs/toolkit";

import {
    Button,
    DialogActions,
    DialogContent,
    TextField,
    CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import { DateTime } from "luxon";

import { useConfirm } from "material-ui-confirm";

import { DeviceList } from "features/devices/DeviceList";
import { GlobalErrorMessage } from "features/core/GlobalErrorMessage";
import { DateRangeControls } from "features/core/dateRange/DateRangeControls";
import { SitesAutocomplete } from "features/sites/SitesAutocomplete";
import { DeviceListVisibilityToggle } from "features/devices/DeviceListVisibilityToggle";
import { DossierCapabilitySelector } from "./DossierCapabilitySelector";

import { Dossier } from "types/Dossier";
import { Device } from "types/Device";
import { DossierDevice } from "types/DossierDevice";

import {
    CreateDossierRequest,
    isCreateDossierRequest,
} from "types/CreateDossierRequest";
import {
    isPatchDossierInformationRequest,
    PatchDossierInformationRequest,
} from "types/PatchDossierInformationRequest";
import { DossierPrioritySelector } from "./DossierPrioritySelector";
import { DossierPriority } from "types/DossierPriority";
import { luxonUtils } from "utils/luxonUtils";

export interface DossierDialogProps {
    onCancel: () => void;
    onSave: (
        draftDossierRequest:
            | CreateDossierRequest
            | PatchDossierInformationRequest,
    ) => void;
    onSiteIdSelected?: (siteId?: EntityId) => void;
    isSaving: boolean;
    existingDossier?: Dossier;
    genericError?: object;
    validationErrors?: Record<string, string[]>;
    availableDevices?: Device[] | DossierDevice[];
    isLoadingAvailableDevices: boolean;
    errorLoadingAvailableDevices?: object;
    maxNumberOfDays: number;
}

export function DossierDialog(props: DossierDialogProps) {
    const [draftDossierRequest, setDraftDossierRequest] = useState<
        CreateDossierRequest | PatchDossierInformationRequest
    >();

    // when a existing dossier is provided we set the initial state to PatchDossierInformationRequest
    useEffect(() => {
        if (props.existingDossier) {
            setDraftDossierRequest({
                ...props.existingDossier,
                selectedDossierDeviceIds: props.existingDossier.dossierDevices
                    .filter((x) => x.isSelected)
                    .map((x) => x.id),
                _patchDossierInformationRequestBrand: {},
            } as unknown as PatchDossierInformationRequest);
        } else {
            setDraftDossierRequest({
                _createDossierRequestBrand: {},
                priority: DossierPriority.Medium,
                selectedDeviceIds: [],
                dossierCapabilityNames: [],
            } as unknown as CreateDossierRequest);
        }
    }, [props.existingDossier]);

    const postReady = useMemo(() => {
        if (!draftDossierRequest) {
            return false;
        }

        let validationResult: boolean =
            !!draftDossierRequest.name &&
            !!draftDossierRequest.priority &&
            !!draftDossierRequest.startTime &&
            !!draftDossierRequest.endTime;

        if (isCreateDossierRequest(draftDossierRequest)) {
            validationResult = validationResult && !!draftDossierRequest.siteId;
        }

        return validationResult;
    }, [draftDossierRequest]);

    // confirm date loss
    const confirm = useConfirm();
    const [
        savingChangesCanResultInDataLoss,
        setSavingChangesCanResultInDataLoss,
    ] = useState<boolean>(false);

    useEffect(() => {
        setSavingChangesCanResultInDataLoss(false);

        if (
            draftDossierRequest &&
            isPatchDossierInformationRequest(draftDossierRequest)
        ) {
            // date range changed
            const checkTimeChange = (
                draft: string | undefined,
                existing: string | undefined,
            ) =>
                draft !== existing &&
                draft &&
                existing &&
                !DateTime.fromISO(draft).equals(DateTime.fromISO(existing));

            if (
                checkTimeChange(
                    draftDossierRequest?.startTime,
                    props.existingDossier?.startTime,
                ) ||
                checkTimeChange(
                    draftDossierRequest?.endTime,
                    props.existingDossier?.endTime,
                )
            ) {
                setSavingChangesCanResultInDataLoss(true);
            }

            // device selection changed
            const selectedDossierDeviceIds =
                props.existingDossier?.dossierDevices
                    .filter((x) => x.isSelected)
                    .map((x) => x.id);
            if (
                draftDossierRequest.selectedDossierDeviceIds.length !==
                    selectedDossierDeviceIds?.length ||
                !draftDossierRequest.selectedDossierDeviceIds.every(
                    (newSelectedDeviceId) =>
                        selectedDossierDeviceIds.some(
                            (existingSelectedDeviceId) =>
                                existingSelectedDeviceId ===
                                newSelectedDeviceId,
                        ),
                )
            ) {
                setSavingChangesCanResultInDataLoss(true);
            }
        }
    }, [props.existingDossier, draftDossierRequest]);

    return draftDossierRequest ? (
        <>
            <DialogContent
                sx={{
                    minWidth: 500,
                }}
            >
                {props.genericError && (
                    <GlobalErrorMessage
                        title={"Unable to save dossier"}
                        message={"Please contact the service desk."}
                        debugData={props.genericError}
                        alertSeverity={"error"}
                    />
                )}

                <TextField
                    autoFocus
                    required
                    margin="none"
                    id="Name"
                    name="name"
                    label="Name"
                    type="text"
                    fullWidth
                    variant="standard"
                    defaultValue={draftDossierRequest.name}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setDraftDossierRequest({
                            ...draftDossierRequest,
                            name: event.target.value,
                        });
                    }}
                    error={
                        props.validationErrors &&
                        props.validationErrors["Name"] !== undefined
                    }
                    helperText={
                        props.validationErrors &&
                        props.validationErrors["Name"]?.join(". ")
                    }
                />

                {isCreateDossierRequest(draftDossierRequest) && (
                    <SitesAutocomplete
                        required
                        minSearchQueryCharacterLength={2}
                        onChange={(selectedSite) => {
                            setDraftDossierRequest({
                                ...draftDossierRequest,
                                siteId: selectedSite?.id,
                            });

                            if (props.onSiteIdSelected) {
                                props.onSiteIdSelected(selectedSite?.id);
                            }
                        }}
                        label="Site"
                        error={
                            props.validationErrors &&
                            props.validationErrors["SiteId"] !== undefined
                        }
                        helperText={
                            props.validationErrors &&
                            props.validationErrors["SiteId"]?.join(". ")
                        }
                        disableClearable
                        margin="dense"
                    />
                )}

                <TextField
                    margin="none"
                    id="ExternalReference"
                    name="externalReference"
                    label="External Reference"
                    type="text"
                    fullWidth
                    variant="standard"
                    defaultValue={draftDossierRequest.externalReference}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setDraftDossierRequest({
                            ...draftDossierRequest,
                            externalReference: event.target.value,
                        });
                    }}
                    slotProps={{
                        htmlInput: {
                            maxLength: 100,
                        },
                    }}
                    error={
                        props.validationErrors &&
                        props.validationErrors["ExternalReference"] !==
                            undefined
                    }
                    helperText={
                        props.validationErrors &&
                        props.validationErrors["ExternalReference"]?.join(". ")
                    }
                />

                <DossierCapabilitySelector
                    defaultValue={
                        props.existingDossier?.dossierCapabilityNames || []
                    }
                    onChange={(dossierCapabilityNames) => {
                        setDraftDossierRequest({
                            ...draftDossierRequest,
                            dossierCapabilityNames,
                        });
                    }}
                    isNewSelection={!props.existingDossier}
                />

                <DossierPrioritySelector
                    defaultValue={
                        props.existingDossier?.priority ||
                        DossierPriority.Medium
                    }
                    onChange={(priority) => {
                        setDraftDossierRequest({
                            ...draftDossierRequest,
                            priority,
                        });
                    }}
                />

                <DateRangeControls
                    defaultSelectedStartDateTime={draftDossierRequest.startTime}
                    defaultSelectedEndDateTime={draftDossierRequest.endTime}
                    required
                    onRangeChange={(
                        isValid: boolean,
                        startDateTime: DateTime | null | undefined,
                        endDateTime: DateTime | null | undefined,
                    ) => {
                        setDraftDossierRequest({
                            ...draftDossierRequest,
                            startTime: isValid
                                ? luxonUtils.toISO(startDateTime)
                                : undefined,
                            endTime: isValid
                                ? luxonUtils.toISO(endDateTime)
                                : undefined,
                        });
                    }}
                    maxNumberOfDays={props.maxNumberOfDays}
                />

                {props.isLoadingAvailableDevices && (
                    <CircularProgress title="Loading available devices..." />
                )}

                {props.errorLoadingAvailableDevices && (
                    <GlobalErrorMessage
                        title={"Unable to fetch available devices"}
                        message={"Please contact the service desk."}
                        debugData={{
                            errorLoadingAvailableDevices:
                                props.errorLoadingAvailableDevices,
                        }}
                        alertSeverity={"error"}
                    />
                )}

                {!props.isLoadingAvailableDevices && props.availableDevices && (
                    <DeviceList
                        devices={props.availableDevices}
                        onToggleDevice={(
                            deviceId: EntityId,
                            selected: boolean,
                        ) => {
                            let selectedDeviceIds =
                                (isCreateDossierRequest(draftDossierRequest)
                                    ? draftDossierRequest.selectedDeviceIds
                                    : draftDossierRequest.selectedDossierDeviceIds) ??
                                [];

                            if (selected) {
                                // new device selected
                                selectedDeviceIds.unshift(deviceId);
                            } else {
                                // device deselected
                                selectedDeviceIds = selectedDeviceIds.filter(
                                    (x) => x !== deviceId,
                                );
                            }

                            if (isCreateDossierRequest(draftDossierRequest)) {
                                setDraftDossierRequest({
                                    ...draftDossierRequest,
                                    selectedDeviceIds,
                                });
                            } else {
                                setDraftDossierRequest({
                                    ...draftDossierRequest,
                                    selectedDossierDeviceIds: selectedDeviceIds,
                                });
                            }
                        }}
                        onToggleSelectAll={(selected: boolean) => {
                            const availableDevices =
                                props.availableDevices?.map((x) => x.id) ?? [];
                            const selectedDeviceIds = selected
                                ? availableDevices
                                : [];

                            if (isCreateDossierRequest(draftDossierRequest)) {
                                setDraftDossierRequest({
                                    ...draftDossierRequest,
                                    selectedDeviceIds,
                                });
                            } else {
                                setDraftDossierRequest({
                                    ...draftDossierRequest,
                                    selectedDossierDeviceIds: selectedDeviceIds,
                                });
                            }
                        }}
                        hideDeviceThumbnail={!!props.existingDossier}
                        selectedDeviceIds={
                            isCreateDossierRequest(draftDossierRequest)
                                ? draftDossierRequest.selectedDeviceIds
                                : draftDossierRequest.selectedDossierDeviceIds
                        }
                        thumbnailRefreshIntervalInMs={0}
                        columnCount={3}
                        deviceListVisibilityToggleComponent={
                            DeviceListVisibilityToggle
                        }
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        // cancel dialog
                        props.onCancel();
                    }}
                >
                    Cancel
                </Button>
                <Button
                    color="primary"
                    disabled={!postReady}
                    onClick={() => {
                        if (savingChangesCanResultInDataLoss) {
                            confirm({
                                description: `Changing the date range or selected devices can result in data loss, are you sure you want to continue?`,
                            }).then((confirmResult) => {
                                if (confirmResult.confirmed) {
                                    props.onSave(draftDossierRequest);
                                }
                            });
                        } else {
                            props.onSave(draftDossierRequest);
                        }
                    }}
                    loading={props.isSaving}
                    loadingPosition="start"
                    startIcon={<SaveIcon />}
                    variant="contained"
                >
                    {props.existingDossier ? "Save" : "Create"}
                </Button>
            </DialogActions>
        </>
    ) : undefined;
}
